import ListFilterPage from '@/pages/ListFilterPage';
import { useServices } from '@/components/ServiceContext';
import React from 'react';
import { sortBooleans, sortMonetaryRanges } from '@lambda-layer/utils/sort.ts';
import {
  FilterSelections,
  RelationalOperator,
} from '@lambda-layer/types/filter.ts';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';

import { ControlType } from '@lambda-layer/models/FilterDefinition/FilterDefinitionFields.ts';

export const boughtListFilters = FilterDefinition.createFilterDefinitions(
  [
    {
      label: 'Top n EPM Score',
      controlType: ControlType.TEXT,
      databaseMetadata: {
        columnName: 'epm3Score1',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'limit',
        columnOrder: 0,
        columnName: 'Top n EPM Score',
      },
      defaultValue: [100_000],
    },
    // Exclude expired records
    {
      label: 'Expiration Date',
      controlType: ControlType.DATE,
      isAdvancedFilter: true,
      databaseMetadata: {
        columnName: 'expirationDate',
        dataType: 'date',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'Expiration Date',
      },
      defaultValue: [new Date(Date.now())],
      defaultRelationalOperator: RelationalOperator.GREATER_THAN_OR_EQUAL,
    },
    // Exclude optOut = 1
    {
      label: 'Opt Out',
      controlType: ControlType.BOOLEAN_TOGGLE,
      isAdvancedFilter: true,
      databaseMetadata: {
        columnName: 'optOut',
        dataType: 'boolean',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'Opt Out',
      },
      defaultValue: [true],
      defaultRelationalOperator: RelationalOperator.NOT_EQUAL,
    },
    // Exclude doNotMail = 1
    {
      label: 'Do Not Mail',
      controlType: ControlType.BOOLEAN_TOGGLE,
      isAdvancedFilter: true,
      databaseMetadata: {
        columnName: 'doNotMail',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'Do Not Mail',
      },
      defaultValue: ['1'], // This is 1 to match the data currently in the database
      defaultRelationalOperator: RelationalOperator.NOT_EQUAL,
    },
    // Exclude hlMatch = 1
    {
      label: 'HL Match',
      controlType: ControlType.BOOLEAN_TOGGLE,
      isAdvancedFilter: true,
      databaseMetadata: {
        columnName: 'hlMatch',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'HL Match',
      },
      defaultValue: ['1'],
      defaultRelationalOperator: RelationalOperator.NOT_EQUAL,
    },
    {
      label: 'List Name',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'listName',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 2,
        columnName: 'ListName',
      },
    },
    {
      label: 'Site',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'site1',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 1,
        columnName: 'Site',
      },
      staffSeedMetadata: {
        dbColumnName: 'community',
      },
    },
    {
      label: 'EPM Grade',
      toolTip:
        'Enterprise Prospect Model score by Acxiom applied to Standard BL purchase twice a year',
      controlType: ControlType.MULTI_SELECT,
      availableOperators: [
        RelationalOperator.LESS_THAN_OR_EQUAL,
        RelationalOperator.GREATER_THAN_OR_EQUAL,
        RelationalOperator.EQUAL,
      ],
      databaseMetadata: {
        columnName: 'epm3Decile1',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 16,
        columnName: 'EPM2 Decile',
      },
    },
    {
      label: 'Distance to Site',
      controlType: ControlType.TEXT,
      availableOperators: [
        RelationalOperator.LESS_THAN_OR_EQUAL,
        RelationalOperator.GREATER_THAN_OR_EQUAL,
      ],
      databaseMetadata: {
        columnName: 'siteDistance1',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 25,
        columnName: 'Distance To Site',
      },
      defaultRelationalOperator: RelationalOperator.LESS_THAN_OR_EQUAL,
    },
    {
      label: 'County',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'county',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 20,
        columnName: 'County',
      },
    },
    {
      label: 'State',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'state',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 9,
        columnName: 'State',
      },
      staffSeedMetadata: {
        dbColumnName: 'state',
      },
    },
    {
      label: 'ZIP5',
      controlType: ControlType.SEARCHABLE_MULTI_SELECT,
      databaseMetadata: {
        columnName: 'zip5',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 28,
        columnName: 'Zip5',
      },
    },
    {
      label: 'ZIP+4',
      controlType: ControlType.SEARCHABLE_MULTI_SELECT,
      databaseMetadata: {
        columnName: 'zipcode',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 10,
        columnName: 'ZipCode',
      },
      staffSeedMetadata: {
        dbColumnName: 'postalCode',
      },
    },
    {
      label: 'HoH Home Value',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'homeValue',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 18,
        columnName: 'HoH Home Value',
      },
      valueSortFn: sortMonetaryRanges,
    },
    {
      label: 'HoH Income',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'householdIncome',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 19,
        columnName: 'Household Income',
      },
      valueSortFn: sortMonetaryRanges,
    },
    {
      label: 'Gender',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'gender',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 27,
        columnName: 'Gender',
      },
    },
    {
      label: 'HoH Age',
      controlType: ControlType.MULTI_SELECT,
      availableOperators: [
        RelationalOperator.LESS_THAN_OR_EQUAL,
        RelationalOperator.GREATER_THAN_OR_EQUAL,
        RelationalOperator.EQUAL,
      ],
      databaseMetadata: {
        columnName: 'age',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 26,
        columnName: 'HoH Age',
      },
    },
    {
      label: 'Net New',
      controlType: ControlType.MULTI_SELECT,
      availableOperators: [RelationalOperator.EQUAL],
      databaseMetadata: {
        columnName: 'netNewFlag',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 22,
        columnName: 'Net New',
        valueMapping: { 0: 'F', 1: 'T' },
      },
      labelMapping: { '0': 'No', '1': 'Yes' },
      valueSortFn: sortBooleans,
    },
    {
      label: 'Batch Code',
      controlType: ControlType.TEXT,
      csvMetadata: {
        fieldType: 'static',
        columnOrder: 11,
        columnName: 'BatchCode',
      },
    },
    {
      label: 'Drop Date',
      controlType: ControlType.DATE,
      csvMetadata: {
        dateFormat: 'MM/dd/yyyy',
        fieldType: 'static',
        columnOrder: 24,
        columnName: 'DropDate',
      },
    },
    {
      label: 'Staff Seed',
      controlType: ControlType.SELECT,
      csvMetadata: {
        fieldType: 'static',
        columnOrder: 0,
        columnName: 'StaffSeed',
      },
      defaultOptions: ['0', '1'],
      defaultValue: ['0'],
      labelMapping: { '0': 'No', '1': 'Yes' },
      valueSortFn: sortBooleans,
    },
    {
      databaseMetadata: {
        columnName: 'saluation', // FIXME: Typo in database column name
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 3,
        columnName: 'LabelSalutation',
      },
      staffSeedMetadata: {
        dbColumnName: 'labelSalutation',
      },
    },
    {
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 4,
        columnName: 'LetterSalutation',
      },
      staffSeedMetadata: {
        dbColumnName: 'letterSalutation',
      },
    },
    {
      databaseMetadata: {
        columnName: 'firstName',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 5,
        columnName: 'FirstName',
      },
      staffSeedMetadata: {
        dbColumnName: 'firstName',
      },
    },
    {
      databaseMetadata: {
        columnName: 'lastName',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 6,
        columnName: 'LastName',
      },
      staffSeedMetadata: {
        dbColumnName: 'lastName',
      },
    },
    {
      databaseMetadata: {
        columnName: 'address',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 7,
        columnName: 'Address',
      },
      staffSeedMetadata: {
        dbColumnName: 'address',
      },
    },
    {
      databaseMetadata: {
        columnName: 'city',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 8,
        columnName: 'City',
      },
      staffSeedMetadata: {
        dbColumnName: 'city',
      },
    },
    {
      databaseMetadata: {
        columnName: 'listName',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 12,
        columnName: 'ListName',
      },
      staffSeedMetadata: {
        staticValue: 'Staff Seed',
      },
    },
    {
      databaseMetadata: {
        columnName: 'listNameId',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 13,
        columnName: 'ListName ID',
      },
      staffSeedMetadata: {
        staticValue: 'Staff Seed',
      },
    },
    {
      databaseMetadata: {
        columnName: 'qseq',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 14,
        columnName: 'qseq',
      },
      staffSeedMetadata: {
        staticValue: '0',
      },
    },
    {
      databaseMetadata: {
        columnName: 'blSelect',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 15,
        columnName: 'BL Select',
      },
      staffSeedMetadata: {
        staticValue: 'Unknown',
      },
    },
    {
      databaseMetadata: {
        columnName: 'epm3Score1',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 17,
        columnName: 'EPM2 Score',
      },
    },
    {
      databaseMetadata: {
        columnName: 'staticControlFlag',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'ControlFlag',
      },
    },
    {
      databaseMetadata: {
        columnName: 'suppress',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'Suppression',
      },
    },
    {
      databaseMetadata: {
        columnName: 'listDate',
        dataType: 'date',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 21,
        columnName: 'List Date',
      },
    },
    {
      databaseMetadata: {
        columnName: 'gender',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 23,
        columnName: 'Gender',
      },
    },
    {
      databaseMetadata: {
        columnName: 'listName',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 29,
        columnName: 'LOC',
        valueMapping: {
          '^CC': 'CC', // If the list name starts with "CC", map it to "CC"
          '.*': 'IND', // Fallback to "IND" for everything else
        },
      },
      staffSeedMetadata: {
        dbColumnName: 'loc',
      },
    },
    {
      databaseMetadata: {
        columnName: 'randomNumber',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 30,
        columnName: 'Random Number',
      },
    },
  ],
  '1.1.1'
);

const exportJobName = (filterSelections: FilterSelections) => {
  const locField = boughtListFilters.find(f => f.label === 'List Name');
  if (locField) {
    const selections = filterSelections[locField.id];
    const selectionValues = selections.value.filter(Boolean);
    if (selectionValues.length) {
      return selectionValues.join(', ');
    }
  }
  return `Bought List Export ${new Date().toISOString()}`;
};

const BoughtListFilterPage: React.FC = () => {
  const { boughtListService } = useServices();

  return (
    <ListFilterPage
      exportJobName={exportJobName}
      listType={'boughtlist'}
      listService={boughtListService}
      filterDefinitions={boughtListFilters}
    />
  );
};

export default BoughtListFilterPage;
